import React, { FC, MouseEvent } from 'react';
import { Card, Space, Typography, List } from 'antd';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { PaymentDetails, Terminal } from '~/types';
import { usePageShow } from '~/hooks/usePageQuery';
import { paymentDetails, terminals } from '~/routes';
import { QrImg } from '~/img';
import { AmountField, TextField } from '~/components';

interface Props {
  data: PaymentDetails;
  terminal: Terminal | undefined;
  loading: boolean;
  hasAccessView: boolean;
  hasAccessTerminalView: boolean;
  className?: string;
}

const PaymentDetailsCard: FC<Props> = (props) => {
  const { data = {}, loading, terminal, className, hasAccessView, hasAccessTerminalView } = props;
  const { name, amount } = data;
  const terminalLabel = terminal?.label;

  const { t } = useTranslation('paymentDetails');
  const { showOpen } = usePageShow(paymentDetails);
  const { showOpen: terminalShowOpen } = usePageShow(terminals);

  const handleTerminalOpen = (e: MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    terminalShowOpen(terminal?.id as string);
  };

  return (
    <Card
      className={className}
      loading={loading}
      bordered={false}
      onClick={() => hasAccessView && showOpen(data.id as string)}
    >
      <List.Item.Meta
        avatar={<QrImg style={{ width: 88, height: 88 }} />}
        title={
          <Typography.Title className="PaymentDetailsCard-Title" level={4}>
            {name}
          </Typography.Title>
        }
        description={
          <Space direction="vertical" className="PaymentDetailsCard-Space">
            <AmountField amount={amount} />
            {terminal && (
              <div>
                {terminalLabel ? (
                  <TextField
                    className="PaymentDetailsCard-LinkedTerminal"
                    label={t('linkedTo')}
                    mode="horizontal"
                  >
                    {hasAccessTerminalView ? (
                      <Typography.Link
                        onClick={handleTerminalOpen}
                        className="PaymentDetailsCard-LinkedTerminal__ellipsis"
                      >
                        {terminalLabel}
                      </Typography.Link>
                    ) : (
                      <div className="PaymentDetailsCard-LinkedTerminal__ellipsis">
                        {terminalLabel}
                      </div>
                    )}
                  </TextField>
                ) : (
                  <Typography.Text type="danger">{t('noLinked')}</Typography.Text>
                )}
              </div>
            )}
          </Space>
        }
      />
    </Card>
  );
};

export default styled(PaymentDetailsCard)`
  ${({ data }) =>
    !data.terminalId &&
    css`
      opacity: 0.5;
    `}
  white-space: nowrap;
  cursor: pointer;
  .ant-card-body {
    padding: 16px;
  }
  .PaymentDetailsCard-Title {
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .PaymentDetailsCard-LinkedTerminal {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    white-space: nowrap;
    .PaymentDetailsCard-LinkedTerminal__ellipsis {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .PaymentDetailsCard-Space {
    width: 100%;
  }
`;
